import { useEffect, useState } from "react";
import {
  Copy, Link2, MessageCircle, Plus, RefreshCw, Trash2,
} from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS_STYLE = {
  sent:       "border-slate-300 text-slate-700 bg-slate-50",
  opened:     "border-blue-300 text-blue-800 bg-blue-50",
  completed:  "border-emerald-300 text-emerald-800 bg-emerald-50",
  expired:    "border-rose-300 text-rose-800 bg-rose-50",
};

export default function KycLinks() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({
    mobile: "", purpose: "tutor", partner_note: "", include_bank: true, expires_in_days: 30,
  });
  const [busy, setBusy] = useState(false);
  const [showShare, setShowShare] = useState(null); // freshly created link
  const [refreshingToken, setRefreshingToken] = useState(null);

  const onboardingUrl = (token) =>
    `${window.location.origin}/onboard/${token}`;

  const waUrl = (mobile, url, partner_note, include_bank) => {
    const lines = [
      "Hi! To complete onboarding for PayFlex payments,",
      `please verify your KYC${include_bank ? " + add a bank account" : ""} via this secure link:`,
      url,
    ];
    if (partner_note) { lines.push("", partner_note); }
    return `https://wa.me/91${mobile}?text=${encodeURIComponent(lines.join("\n"))}`;
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/kyc-links");
      setItems(res.data?.items || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load links");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createLink = async () => {
    if (!/^[6-9]\d{9}$/.test(draft.mobile)) { toast.error("Enter a valid 10-digit mobile"); return; }
    setBusy(true);
    try {
      const res = await api.post("/kyc-links", draft);
      setShowShare({ ...res.data, url: onboardingUrl(res.data.token) });
      setCreateOpen(false);
      setDraft({ mobile: "", purpose: "tutor", partner_note: "", include_bank: true, expires_in_days: 30 });
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  const refreshOne = async (token) => {
    setRefreshingToken(token);
    try {
      const { data } = await api.post(`/kyc-links/public/${token}/refresh-kyc`);
      toast.success(`KYC ${data.verification_percentage}% · ${data.is_verified ? "verified" : "pending"}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Refresh failed");
    } finally {
      setRefreshingToken(null);
    }
  };

  const revoke = async (token) => {
    if (!window.confirm("Revoke this link? The target won't be able to open it anymore.")) return;
    try {
      await api.delete(`/kyc-links/${token}`);
      toast.success("Link revoked");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Revoke failed");
    }
  };

  const copy = async (txt) => {
    try { await navigator.clipboard.writeText(txt); toast.success("Copied"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <DashboardLayout>
    <div className="space-y-6 animate-fade-up" data-testid="kyc-links-page">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Link2 className="size-6" /> KYC links
          </h1>
          <p className="text-sm text-muted-foreground">
            Send a tracked KYC + bank-onboarding link to any mobile. Open count, latest verification %, and completion status auto-refresh whenever the target finishes a step at hitch.zone.
          </p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-kyc-link-btn"><Plus className="size-4" /> Create link</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create KYC link</DialogTitle>
              <DialogDescription>
                We'll generate a unique URL. Share it via WhatsApp / SMS — the target will be walked through OTP → PAN → Aadhaar → bank-add.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Mobile (10-digit)</Label>
                <Input
                  value={draft.mobile}
                  onChange={(e) => setDraft({ ...draft, mobile: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                  maxLength={10}
                  className="font-mono"
                  placeholder="e.g. 9876543210"
                  data-testid="create-kyc-mobile"
                />
              </div>
              <div>
                <Label>Purpose</Label>
                <Select value={draft.purpose} onValueChange={(v) => setDraft({ ...draft, purpose: v })}>
                  <SelectTrigger data-testid="create-kyc-purpose"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tutor">Tutor / institution</SelectItem>
                    <SelectItem value="student">Student / parent</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="vendor">Vendor</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Note (optional, shown in WhatsApp message)</Label>
                <Textarea
                  value={draft.partner_note}
                  onChange={(e) => setDraft({ ...draft, partner_note: e.target.value })}
                  maxLength={300}
                  placeholder="e.g. For student Aisha's monthly fees"
                  data-testid="create-kyc-note"
                />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div className="text-sm">
                  Include bank-account step
                  <div className="text-[11px] text-muted-foreground">After KYC, prompt the user to add their bank.</div>
                </div>
                <Switch
                  checked={draft.include_bank}
                  onCheckedChange={(v) => setDraft({ ...draft, include_bank: v })}
                  data-testid="create-kyc-include-bank"
                />
              </div>
              <div>
                <Label>Expires in (days)</Label>
                <Input
                  type="number"
                  min={1} max={365}
                  value={draft.expires_in_days}
                  onChange={(e) => setDraft({ ...draft, expires_in_days: Number(e.target.value) || 30 })}
                  data-testid="create-kyc-expiry"
                />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={createLink} disabled={busy} data-testid="confirm-create-kyc">
                {busy ? "Creating…" : "Create & share"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Share modal — opens right after create */}
      <Dialog open={!!showShare} onOpenChange={(v) => !v && setShowShare(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Link ready</DialogTitle>
            <DialogDescription>
              Send the link via WhatsApp or copy-paste it anywhere. We'll track opens + KYC progress on this page.
            </DialogDescription>
          </DialogHeader>
          {showShare && (
            <div className="space-y-3">
              <div className="rounded-md border bg-muted/40 p-3 font-mono text-xs break-all" data-testid="share-link-url">
                {showShare.url}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => copy(showShare.url)} data-testid="share-copy-btn">
                  <Copy className="size-4" /> Copy
                </Button>
                <a
                  href={waUrl(showShare.mobile, showShare.url, showShare.partner_note, showShare.include_bank)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-md border border-emerald-500 text-emerald-700 hover:bg-emerald-50 px-4 py-2 text-sm font-medium"
                  data-testid="share-whatsapp-btn"
                >
                  <MessageCircle className="size-4 mr-1" /> WhatsApp
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent links</CardTitle>
          <CardDescription>Click "Refresh" on any row to pull the latest KYC % from hitch.zone.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
          ) : items.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No links yet — click "Create link" above.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mobile · Purpose</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Opens</TableHead>
                    <TableHead>KYC %</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((it) => (
                    <TableRow key={it.token} data-testid={`kyc-link-row-${it.token}`}>
                      <TableCell className="font-mono text-xs">
                        +91 {it.mobile}
                        <div className="text-[10px] uppercase text-muted-foreground">{it.purpose}</div>
                        {it.partner_note && (
                          <div className="text-[11px] text-muted-foreground italic mt-0.5 max-w-[200px] truncate">{it.partner_note}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[10px] uppercase ${STATUS_STYLE[it.status] || ""}`}>
                          {it.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{it.open_count || 0}</TableCell>
                      <TableCell className="font-mono text-xs">
                        {it.last_kyc_percentage === null || it.last_kyc_percentage === undefined ? "—" : `${it.last_kyc_percentage}%`}
                      </TableCell>
                      <TableCell className="text-[11px] text-muted-foreground">
                        {it.created_at ? new Date(it.created_at).toLocaleDateString() : "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => copy(onboardingUrl(it.token))}
                            title="Copy link"
                            data-testid={`kyc-link-copy-${it.token}`}
                          >
                            <Copy className="size-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => refreshOne(it.token)}
                            disabled={refreshingToken === it.token || it.status === "expired"}
                            title="Refresh KYC"
                            data-testid={`kyc-link-refresh-${it.token}`}
                          >
                            <RefreshCw className={`size-4 ${refreshingToken === it.token ? "animate-spin" : ""}`} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => revoke(it.token)}
                            disabled={it.status === "expired"}
                            title="Revoke"
                            data-testid={`kyc-link-revoke-${it.token}`}
                          >
                            <Trash2 className="size-4 text-rose-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </DashboardLayout>
  );
}
